/* eslint-disable jest/require-hook */
import { Button, Col, Form, Input, Result, Row, Select } from "antd";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

import { AppletProps, FormikRequestBodyProps } from "./types";

import { analyticsEvent } from "~/Analytics";
import { AppletWrapper } from "~/Atom/AppletWrapper";
import { EMAIL_REGEX } from "~/Constant";
import { useFlag } from "~/LaunchDarkly";
import { useOmnichannelApi } from "~/OmnichannelApi";
import { Paths } from "~/OmnichannelApi/openapi";

const Applet: React.FC<AppletProps> = ({
  showLocationField,
  marketId,
  inboxType,
  contactType,
  inboxEmailAddress,
}) => {
  const hasAuthorization = useFlag("applet-es-contact-form");

  const api = useOmnichannelApi();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [locationsList, setLocationsList] = useState<string[]>([]);

  const formik = useFormik<FormikRequestBodyProps>({
    initialValues: {
      contact_form_url: window.location.href,
      first_name: "",
      last_name: "",
      email_address: "",
      phone_number: "",
      message: "",
      contact_type: contactType,
      market_id: marketId ? Number(marketId) : undefined,
      inbox_type: inboxType ?? undefined,
      inbox_email_address: inboxEmailAddress ?? undefined,
      location_name: undefined,
    },
    onSubmit: (values) => {
      const submissionValues = { ...values };

      if (
        !submissionValues.inbox_email_address &&
        !submissionValues.inbox_type &&
        !submissionValues.market_id
      ) {
        submissionValues.inbox_email_address = "service@equipmentshare.com";
      } else if (submissionValues.inbox_email_address === "anshul") {
        //Can be retired with Datacenters Campaign (today: 3/11/25)
        submissionValues.inbox_email_address =
          "anshul.sharma@equipmentshare.com";
      }

      api["contact-form-submissions.create"](
        {},
        submissionValues as Paths.ContactFormSubmissionsCreate.RequestBody
      )
        .then((res) => {
          setIsSubmitted(true);
        })
        .catch(console.error);
    },
  });

  const _submitForm = () => {
    const errors: Partial<
      FormikProps<Paths.ContactFormSubmissionsCreate.RequestBody>["errors"] &
        Record<string, string>
    > = {};

    if (!formik.values.first_name.trim().length) {
      errors.first_name = "Required";
    }
    if (!formik.values.last_name.trim().length) {
      errors.last_name = "Required";
    }
    if (!formik.values.email_address.trim().length) {
      errors.email_address = "Required";
    } else if (!EMAIL_REGEX.test(formik.values.email_address.trim())) {
      errors.email_address = "Valid email address required";
    }
    if (!formik.values.phone_number.trim().length) {
      errors.phone_number = "Required";
    } else if (
      !/^\d{10,}/.test(formik.values.phone_number.replace(/\D/g, ""))
    ) {
      errors.phone_number = "Valid phone number required";
    }

    if (!formik.values.message.trim().length) {
      errors.message = "Required";
    } else if (formik.values.message.trim().length <= 10) {
      errors.message = "Messages must be more than 10 characters";
    }

    formik.setErrors(errors);

    const errorLength = Object.keys(errors).length;

    if (!errorLength) {
      formik.handleSubmit();
    }
  };

  useEffect(() => {
    if (formik.dirty) {
      setIsDirty(true);
    }
  }, [formik.dirty]);

  useEffect(() => {
    if (isDirty) {
      analyticsEvent("contact_request_start", {
        market_id: marketId,
        inbox_type: inboxType,
        contact_type: contactType,
        email_inbox_address: inboxEmailAddress,
        contact_form_url: window.location.href,
      });
    }
  }, [isDirty]);

  useEffect(() => {
    if (isSubmitted) {
      analyticsEvent("contact_request_finish", {
        market_id: marketId,
        inbox_type: inboxType,
        contact_type: contactType,
        email_inbox_address: inboxEmailAddress,
        contact_form_url: window.location.href,
      });
    }
  }, [isSubmitted]);

  useEffect(() => {
    if (showLocationField !== undefined) {
      api["location-finder.list"]().then((res) => {
        const locationsNames = res.data.locations
          .map((location) => location.name)
          .filter(Boolean) // optional: removes null/undefined names
          .sort((a, b) => a.localeCompare(b));

        setLocationsList(locationsNames);
      });
    }
  }, [showLocationField]);

  if (!hasAuthorization) {
    return null;
  }

  return (
    <AppletWrapper appletName="contact-form">
      {isSubmitted ? (
        <Result
          status="success"
          title="Message Submitted"
          subTitle="Thank you for contacting us! We have received your message and will be reviewing it shortly."
          className="fadeInFast"
        />
      ) : (
        <Form layout="vertical" style={{ display: "block" }}>
          <Row
            justify="center"
            gutter={16}
            style={{ maxWidth: "50rem", margin: "auto" }}
          >
            <Col xs={24} md={12}>
              <Form.Item
                label="First Name"
                validateStatus={formik.errors.first_name ? "error" : "success"}
                help={formik.errors.first_name}
              >
                <Input
                  type="text"
                  name="first_name"
                  placeholder="Enter first name"
                  onChange={formik.handleChange}
                  value={formik.values.first_name}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Last Name"
                validateStatus={formik.errors.last_name ? "error" : "success"}
                help={formik.errors.last_name}
              >
                <Input
                  type="text"
                  name="last_name"
                  placeholder="Enter last name"
                  onChange={formik.handleChange}
                  value={formik.values.last_name}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Email Address"
                validateStatus={
                  formik.errors.email_address ? "error" : "success"
                }
                help={formik.errors.email_address}
              >
                <Input
                  type="text"
                  name="email_address"
                  placeholder="Enter email address"
                  onChange={formik.handleChange}
                  value={formik.values.email_address}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Phone Number"
                validateStatus={
                  formik.errors.phone_number ? "error" : "success"
                }
                help={formik.errors.phone_number}
              >
                <Input
                  type="text"
                  name="phone_number"
                  placeholder="Enter phone number"
                  onChange={formik.handleChange}
                  value={formik.values.phone_number}
                />
              </Form.Item>
            </Col>
            {showLocationField && (
              <Col xs={24}>
                <Form.Item label="Location Field">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={locationsList.map((location) => ({
                      label: location,
                      value: location,
                    }))}
                    placeholder="Select a location"
                    onChange={(value) =>
                      formik.setFieldValue("location_name", value)
                    }
                    value={formik.values.location_name}
                    notFoundContent="No locations found"
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={24}>
              <Form.Item
                label="Your Message"
                validateStatus={formik.errors.message ? "error" : "success"}
                help={formik.errors.message}
              >
                <Input.TextArea
                  name="message"
                  placeholder="Enter your message"
                  onChange={formik.handleChange}
                  value={formik.values.message}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={24} style={{ textAlign: "center" }}>
              <Button
                type="primary"
                loading={formik.isSubmitting}
                onClick={_submitForm}
                size="large"
              >
                {"Submit Message"}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </AppletWrapper>
  );
};

export const mount = (el: HTMLDivElement) => {
  const root = createRoot(el);

  root.render(
    <Applet
      showLocationField={
        (el.getAttribute(
          "show-location-field"
        ) as AppletProps["showLocationField"]) || undefined
      }
      contactType={
        (el.getAttribute("contact-type") as AppletProps["contactType"]) ||
        undefined
      }
      marketId={el.getAttribute("market-id") || undefined}
      inboxType={
        (el.getAttribute("inbox-type") as AppletProps["inboxType"]) || undefined
      }
      inboxEmailAddress={
        (el.getAttribute(
          "inbox-email-address"
        ) as AppletProps["inboxEmailAddress"]) || undefined
      }
    />
  );
};
